import { useState } from 'react'
import { useCVGenerationStore } from '../../stores/cvGenerationStore'

export default function InterrogationStep() {
  const { session, loadState, respond, skipAndAssemble, assemble } =
    useCVGenerationStore()
  const loading = loadState === 'loading'
  const [answer, setAnswer] = useState('')

  if (!session) return null

  const { state } = session
  const currentQuestion = state.current_question
  const qaHistory = state.qa_history || []
  const interrogationComplete = state.interrogation_complete
  const questionCount = session.question_count
  const maxQuestions = session.max_questions

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!currentQuestion || !answer.trim() || loading) return
    const trimmed = answer.trim()
    setAnswer('')
    await respond({ questionId: currentQuestion.id, answer: trimmed, skipped: false })
  }

  async function handleSkipQuestion() {
    if (!currentQuestion || loading) return
    await respond({ questionId: currentQuestion.id, answer: null, skipped: true })
  }

  async function handleAssemble() {
    await assemble()
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left column — Q&A history */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <h1 className="text-2xl font-['IBM_Plex_Serif'] font-semibold text-[#1a1c1a] tracking-tight">
              A few questions before we build your CV
            </h1>
            <p className="text-sm text-[#545f6e] mt-2">
              These help us fill gaps and verify details for a stronger application.
            </p>
          </div>

          {/* Answered / skipped questions */}
          {qaHistory.map((qa, idx) => (
            <div key={qa.question_id} className="space-y-2">
              <div className="bg-white rounded-lg border border-[#dac2b6]/20 p-4">
                <p className="text-xs font-medium text-[#545f6e] uppercase tracking-wider mb-1">
                  Question {idx + 1}
                </p>
                <p className="text-sm text-[#1a1c1a]">{qa.question}</p>
              </div>
              <div className="ml-4 pl-4 border-l-2 border-[#833c0b]/30">
                {qa.skipped
                  ? <p className="text-sm text-[#877369] italic">Skipped</p>
                  : <p className="text-sm text-[#54433a] italic">{qa.answer}</p>
                }
              </div>
            </div>
          ))}

          {/* Interrogation complete message */}
          {interrogationComplete && !loading && session.current_step !== 'cv.assembly' && (
            <div className="bg-[#f4f3f0] rounded-lg p-6 text-center space-y-4">
              <p className="text-sm text-[#1a1c1a] font-medium">
                {qaHistory.length > 0 && qaHistory[qaHistory.length - 1].skipped
                  ? "No more questions — ready to assemble your CV."
                  : qaHistory.length > 0
                    ? "We have enough information to assemble your CV."
                    : "Your library covers this role well. Ready to assemble."}
              </p>
              <button
                onClick={handleAssemble}
                className="px-6 py-3 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] transition-all shadow-sm hover:shadow-md"
              >
                Assemble CV →
              </button>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="bg-[#f4f3f0] rounded-lg p-6 flex items-center justify-center gap-3">
              <span className="animate-spin inline-block w-5 h-5 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
              <p className="text-sm text-[#545f6e]">
                {session.current_step === 'cv.assembly'
                  ? 'Assembling your CV...'
                  : 'Thinking...'}
              </p>
            </div>
          )}
        </div>

        {/* Right column — Current question */}
        <div className="lg:col-span-5">
          {currentQuestion && !interrogationComplete && (
            loading ? (
              <div className="sticky top-32 bg-white rounded-xl border border-[#dac2b6]/20 p-6 flex flex-col items-center justify-center gap-4 min-h-[200px] shadow-[0px_4px_20px_rgba(47,49,47,0.04)]">
                <span className="animate-spin inline-block w-6 h-6 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
                <p className="text-sm text-[#545f6e]">Analysing your impact...</p>
              </div>
            ) : (
              <>
                <div className="sticky top-32 bg-white rounded-xl border border-[#dac2b6]/20 p-6 space-y-4 shadow-[0px_4px_20px_rgba(47,49,47,0.04)]">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-[#833c0b] uppercase tracking-wider">
                      Question {questionCount}{maxQuestions ? ` · up to ${maxQuestions}` : ''}
                    </p>
                    <span className="text-xs text-[#877369] bg-[#f4f3f0] px-2 py-0.5 rounded">
                      {currentQuestion.category.replace(/_/g, ' ')}
                    </span>
                  </div>

                  {/* Conflict sources */}
                  {currentQuestion.conflict_sources &&
                    currentQuestion.conflict_sources.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-amber-700 flex items-center gap-1">
                          <span className="material-symbols-outlined text-base">warning</span>
                          Conflicting sources detected
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {currentQuestion.conflict_sources.map((src, i) => (
                            <div key={i} className="bg-white rounded p-2">
                              <p className="text-[10px] font-medium text-amber-600 uppercase">
                                {src.label}
                              </p>
                              <p className="text-xs text-[#54433a] mt-1">{src.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {currentQuestion.context && (
                    <p className="text-xs text-[#877369]">{currentQuestion.context}</p>
                  )}

                  <p className="text-sm text-[#1a1c1a] font-medium leading-relaxed">
                    {currentQuestion.text}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <textarea
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Type your answer..."
                      rows={5}
                      disabled={loading}
                      className="w-full bg-[#f4f3f0] border-0 rounded-lg px-4 py-3 text-sm text-[#1a1c1a] placeholder:text-[#877369] focus:outline-none focus:ring-2 focus:ring-[#833c0b]/20 resize-y disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={handleSkipQuestion}
                        disabled={loading}
                        className="text-xs text-[#545f6e] hover:text-[#1a1c1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Skip this question
                      </button>
                      <button
                        type="submit"
                        disabled={!answer.trim() || loading}
                        className="px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Send Response →
                      </button>
                    </div>
                  </form>

                  <div className="pt-2 border-t border-[#dac2b6]/20">
                    <p className="text-[10px] text-[#877369]">
                      Tip: Be specific with numbers, timelines, and outcomes.
                    </p>
                  </div>
                </div>

                {/* Skip remaining & assemble shortcut */}
                <div className="mt-4 text-center">
                  <button
                    onClick={skipAndAssemble}
                    disabled={loading}
                    className="text-xs text-[#545f6e] hover:text-[#833c0b] transition-colors underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Skip remaining & assemble
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}
